/**
 * CSV Export Utility
 *
 * Export job data to CSV format for download.
 *
 * DDD Note: This is a PRESENTATION concern - transforming
 * view models into a different display format (CSV).
 * Belongs in frontend, not backend.
 */

import Papa from 'papaparse';
import type { JobCardViewModel } from '@/application/service-provider/view-models/JobListViewModel';

/**
 * Export jobs to CSV file
 *
 * Transforms job view models into CSV format and triggers browser download.
 * All data comes from already-fetched view models - no backend round-trip needed.
 *
 * @param jobs - Array of job view models to export
 */
export function exportJobsToCSV(jobs: JobCardViewModel[]): void {
  const csvData = jobs.map(job => ({
    'Job Number': job.displayName,
    'Address': job.displayAddress,
    'Status': job.statusBadge.label,
    'Scheduled Date': job.scheduleText,
    'Start Date': job.startDate ?? 'N/A',
    'Target Completion': job.targetCompletionDate ?? 'N/A',
    'Service Type': job.serviceType ?? 'N/A',
    'Container Size': job.containerSize ?? 'N/A',
    'Material Type': job.materialType ?? 'N/A',
    'Source': job.source === 'marketplace' ? 'Marketplace' : 'Direct',
    'Container': job.assignedContainer?.displayName ?? 'Not Assigned',
    'Vehicle': job.assignedVehicle?.displayName ?? 'Not Assigned',
    'Driver': job.assignedDriver?.name ?? 'Not Assigned',
    'Project Type': job.projectType ?? 'N/A',
    'Estimated Waste': job.estimatedWaste ?? 'N/A',
    'Urgency Level': job.urgencyLevel,
    'Days Remaining': job.daysRemaining !== undefined ? job.daysRemaining.toString() : 'N/A',
    'Last Updated': new Date(job.lastUpdatedAt).toLocaleDateString(),
  }));

  const csv = Papa.unparse(csvData);

  // Trigger download
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');
  const url = URL.createObjectURL(blob);

  link.setAttribute('href', url);
  link.setAttribute('download', `calwmp-jobs-${new Date().toISOString().split('T')[0]}.csv`);
  link.style.visibility = 'hidden';

  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);

  // Clean up the object URL
  URL.revokeObjectURL(url);
}

/**
 * Export only selected jobs to CSV
 *
 * Filters jobs by selected IDs before exporting.
 *
 * @param allJobs - Full array of job view models
 * @param selectedIds - Set of job IDs to export
 */
export function exportSelectedJobsToCSV(
  allJobs: JobCardViewModel[],
  selectedIds: Set<string>
): void {
  const selectedJobs = allJobs.filter(job => selectedIds.has(job.id));

  if (selectedJobs.length === 0) {
    console.warn('No jobs selected for export');
    return;
  }

  exportJobsToCSV(selectedJobs);
}

/**
 * Download helper for any CSV content
 *
 * @param content - CSV string content
 * @param filename - Desired filename (without extension)
 */
export function downloadCSV(content: string, filename: string): void {
  const blob = new Blob([content], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');
  const url = URL.createObjectURL(blob);

  link.setAttribute('href', url);
  link.setAttribute('download', `${filename}.csv`);
  link.style.visibility = 'hidden';

  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);

  URL.revokeObjectURL(url);
}
